import { useEffect, useMemo, useState } from 'react';
import { Calendar, Eye, User as UserIcon } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useLang } from '../../LangContext';
import { statusColor } from '../../utils/statusColor';
import { statusLabel } from '../../utils/statusLabel';
import { formatShortDate } from '../../utils/dateFormat';
import { useSubmissionFilters } from './submissions/useSubmissionFilters';
import SubmissionFilterBar from './submissions/SubmissionFilterBar';
import SubmissionDetailModal from './submissions/SubmissionDetailModal';

// Siparişleri statüler arasında sürükle-bırak ile yönetmek için ayrı bir ekran.
// "Tamamlandı" statüsü kanban akışının dışında tutulur (pasif) — yalnızca
// "Tamamlananları göster" filtresi açıkken veya statü filtresiyle seçilince kolon olarak belirir.
export default function SubmissionKanbanPage({ submissions, statuses, forms, loadSubmission, changeStatus, clearSubmissionFiles }) {
  const { locale: t, lang } = useLang();
  // Sürükle-bırağın optimistic güncellemesi için yerel ayna
  const [localSubmissions, setLocalSubmissions] = useState(submissions || []);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    setLocalSubmissions(submissions || []);
  }, [submissions]);

  const {
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    formOptions,
    userOptions,
    filteredSubmissions,
    activeFilterCount,
    toggleMulti,
    resetFilters,
    completedStatusId,
  } = useSubmissionFilters(localSubmissions, forms, statuses);

  // Tamamlandı kolonu yalnızca "göster" açıkken veya statü filtresiyle açıkça seçilince görünür
  const visibleStatuses = useMemo(() => {
    if (completedStatusId == null) return statuses || [];
    const showCompletedColumn = filters.showCompleted || filters.statusIds.includes(completedStatusId);
    return (statuses || []).filter((status) => status.id !== completedStatusId || showCompletedColumn);
  }, [statuses, completedStatusId, filters.showCompleted, filters.statusIds]);

  async function openSubmission(id) {
    try {
      const detail = await loadSubmission(id);
      if (detail) setSelectedSubmission(detail);
    } catch {
      // loadSubmission kendi alert'ini gösterir
    }
  }

  // Kanban DnD: 5px sürükleme eşiği ile tıklama/sürükleme ayrımı
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;
    const submissionId = String(active.id);
    const targetStatusId = Number(over.id);
    const current = localSubmissions.find((item) => item.id === submissionId);
    if (!current || current.statusId === targetStatusId) return;

    const targetStatus = (statuses || []).find((status) => status.id === targetStatusId);
    if (!targetStatus) return;

    // Optimistic güncelleme — başarısız olursa geri al
    const previous = localSubmissions;
    setLocalSubmissions((items) =>
      items.map((item) =>
        item.id === submissionId
          ? { ...item, statusId: targetStatusId, status: targetStatus.name, statusCode: targetStatus.code }
          : item,
      ),
    );

    try {
      const result = await changeStatus(submissionId, targetStatusId);
      if (!result) setLocalSubmissions(previous);
    } catch {
      setLocalSubmissions(previous);
    }
  }

  return (
    <section className="section page-panel sp-page">
      <div className="section-header sp-header">
        <div>
          <h2>{t.submissions.kanbanTitle}</h2>
          <p className="muted">{t.submissions.kanbanSubtitle}</p>
        </div>
        <div className="sp-header-actions">
          <span className="metric-pill">{t.submissions.records(filteredSubmissions.length, submissions.length)}</span>
        </div>
      </div>

      <SubmissionFilterBar
        filters={filters}
        setFilters={setFilters}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        formOptions={formOptions}
        userOptions={userOptions}
        statuses={statuses}
        activeFilterCount={activeFilterCount}
        toggleMulti={toggleMulti}
        resetFilters={resetFilters}
      />

      <SubmissionKanbanView
        items={filteredSubmissions}
        statuses={visibleStatuses}
        onOpen={openSubmission}
        sensors={sensors}
        onDragEnd={handleDragEnd}
        t={t.submissions}
        lang={lang}
      />

      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          statuses={statuses}
          onClose={() => setSelectedSubmission(null)}
          onChangeStatus={changeStatus}
          onClearFiles={clearSubmissionFiles}
          onUpdated={setSelectedSubmission}
          lang={lang}
        />
      )}
    </section>
  );
}

// ----- Kanban görünümü -----
function SubmissionKanbanView({ items, statuses, onOpen, sensors, onDragEnd, t, lang }) {
  // Statü id -> kart listesi
  const grouped = useMemo(() => {
    const map = new Map();
    statuses.forEach((status) => map.set(status.id, []));
    items.forEach((item) => {
      if (!map.has(item.statusId)) map.set(item.statusId, []);
      map.get(item.statusId).push(item);
    });
    return map;
  }, [items, statuses]);

  if (statuses.length === 0) {
    return <div className="empty-state">{t.noStatuses}</div>;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="sp-kanban">
        {statuses.map((status) => (
          <KanbanColumn
            key={status.id}
            status={status}
            items={grouped.get(status.id) || []}
            onOpen={onOpen}
            t={t}
            lang={lang}
          />
        ))}
      </div>
    </DndContext>
  );
}

function KanbanColumn({ status, items, onOpen, t, lang }) {
  const { locale } = useLang();
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  const color = statusColor(status.code);

  return (
    <div className={`sp-kanban-col${isOver ? ' is-over' : ''}`}>
      <div className="sp-kanban-col-head" style={{ borderTopColor: color.text }}>
        <span className="sp-kanban-col-title">
          <span className="sp-chip-dot" style={{ background: color.text }} />
          {statusLabel(locale, status.code, status.name)}
        </span>
        <span className="sp-kanban-col-count">{items.length}</span>
      </div>
      <div ref={setNodeRef} className="sp-kanban-col-body">
        {items.length === 0 && <div className="sp-kanban-empty">{t.kanbanEmpty}</div>}
        {items.map((item) => (
          <KanbanCard key={item.id} item={item} onOpen={onOpen} color={color} t={t} lang={lang} />
        ))}
      </div>
    </div>
  );
}

function KanbanCard({ item, onOpen, color, t, lang }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.55 : 1,
    borderLeftColor: color.text,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sp-kanban-card${isDragging ? ' is-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div className="sp-kanban-card-head">
        <strong title={item.formName}>{item.formName}</strong>
        <button
          type="button"
          className="sp-kanban-card-open"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onOpen(item.id)}
          title={t.inspect}
        >
          <Eye size={14} />
        </button>
      </div>
      <div className="sp-kanban-card-meta">
        <span><UserIcon size={12} /> {item.userName}</span>
        <span><Calendar size={12} /> {formatShortDate(item.submittedAt, lang)}</span>
      </div>
    </div>
  );
}
